import React from "react";
import clsx from "clsx";

import WComponent from "../../common/WComponent";
import PageHeading from "../../common/page_heading/PageHeading";
import BackofficeService from "../../../services/BackofficeService";
import UtilityService from "../../../services/UtilityService";
import TemplateEditorModal from "./TemplateEditorModal";
import { I } from "../common/icons";
import { SERVICES, SERVICE_ORDER } from "../backofficeConstants";

import "../backoffice.css";

const { alert } = UtilityService;

/**
 * Settings › Backoffice Templates: the task templates "Prepopulate from template" offers,
 * one or more per service.
 */
class BackofficeTemplates extends WComponent {
	state = { loading: true, templates: [], editing: null, showInactive: false, search: "" };

	async componentDidMount() {
		await this.load();
	}

	load = async () => {
		await this.update({ loading: true });
		const result = await BackofficeService.listTemplates({ includeInactive: true });
		await this.update({ loading: false, templates: result.ok ? result.data : [] });
		if (!result.ok) alert({ type: "error", text: result.error });
	};

	onSaved = async () => {
		await this.update({ editing: null });
		await this.load();
		alert({ type: "success", text: "Template saved" });
	};

	toggleActive = async tpl => {
		const result = await BackofficeService.updateTemplate({ templateId: tpl.id, status: tpl.status === "active" ? "inactive" : "active" });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.load();
	};

	remove = async tpl => {
		if (!window.confirm(`Delete “${tpl.name}”? Tasks already added to clients are kept.`)) return;
		const result = await BackofficeService.removeTemplate({ templateId: tpl.id });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.load();
		alert({ type: "success", text: "Template deleted" });
	};

	duplicate = tpl => {
		this.update({ editing: Object.assign({}, tpl, { id: null, name: `${tpl.name} (copy)`, isDefault: false, tasks: tpl.tasks.map(t => Object.assign({}, t, { id: null })) }) });
	};

	render() {
		const { loading, templates, editing, showInactive, search } = this.state;
		const q = search.trim().toLowerCase();
		const visible = templates.filter(t => (showInactive || t.status === "active") && (!q || t.name.toLowerCase().includes(q) || SERVICES[t.service].label.toLowerCase().includes(q)));

		return (
			<div className="bo-settings">
				<PageHeading section="Settings" title="Backoffice Templates" />
				<div className="bo-toolbar" style={{ marginTop: 16 }}>
					<div className="bo-filters">
						<div className="bo-search">
							<I.search />
							<input type="text" placeholder="Search templates" value={search} onChange={e => this.update({ search: e.target.value })} />
						</div>
						<label className="bo-chk">
							<input type="checkbox" checked={showInactive} onChange={e => this.update({ showInactive: e.target.checked })} /> Show inactive
						</label>
					</div>
					<button className="bo-btn bo-btn--primary ft-bo-template-new" type="button" onClick={() => this.update({ editing: { id: null, name: "", service: "monthend", description: "", tasks: [] } })}>
						<I.plusPlain />
						New template
					</button>
				</div>

				<div className="bo-tpl-table">
					<div className="bo-tpl-row bo-tpl-row--head">
						<span />
						<span>Template</span>
						<span>Service</span>
						<span>Tasks</span>
						<span>Status</span>
						<span />
					</div>
					{loading && <div className="bo-tpl-row bo-muted">Loading…</div>}
					{!loading && !visible.length && <div className="bo-tpl-row bo-muted">No templates{q ? ` match “${search}”` : ""}.</div>}
					{SERVICE_ORDER.flatMap(s => visible.filter(t => t.service === s)).map(tpl => (
						<div key={tpl.id} className="bo-tpl-row">
							<span className="bo-svc-swatch" style={{ background: SERVICES[tpl.service].color }} />
							<span>
								<b>{tpl.name}</b>
								{tpl.isDefault && <span className="bo-pill bo-pill--bo bo-pill--xs" style={{ marginLeft: 8 }}>Default</span>}
								<small>{tpl.description || `${tpl.tasks.filter(t => t.email).length} email automation${tpl.tasks.filter(t => t.email).length === 1 ? "" : "s"}`}</small>
							</span>
							<span>{SERVICES[tpl.service].label}</span>
							<span>{tpl.tasks.length}</span>
							<span>
								<span className={clsx("bo-pill", tpl.status === "active" ? "bo-pill--green" : "bo-pill--grey")}>{tpl.status}</span>
							</span>
							<span className="bo-tacts">
								<button className="bo-btn bo-btn--sm" type="button" onClick={() => this.update({ editing: tpl })}>
									<I.edit />
									Edit
								</button>
								<button className="bo-btn bo-btn--sm" type="button" onClick={() => this.duplicate(tpl)} title="Duplicate">
									<I.file />
									Duplicate
								</button>
								<button className="bo-btn bo-btn--sm" type="button" onClick={() => this.toggleActive(tpl)}>
									{tpl.status === "active" ? "Deactivate" : "Activate"}
								</button>
								<button className="bo-ico bo-ico--del" type="button" onClick={() => this.remove(tpl)} aria-label="Delete">
									<I.trash />
								</button>
							</span>
						</div>
					))}
				</div>
				<div className="bo-cap-note">
					Task dates in a template are day offsets from the period anchor: for month-end the anchor is the last day of the month being closed (so −5 is five days before month end and +3 is three days into the next month); for payroll and AP it is
					the first day of the pay period or week.
				</div>

				{editing && <TemplateEditorModal template={editing} onClose={() => this.update({ editing: null })} onSaved={this.onSaved} />}
			</div>
		);
	}
}

export default BackofficeTemplates;
